import {Panel} from '@github-ui/docked-side-panel/Panel'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {SidePanel} from '@github-ui/side-panel'
import {CommentIcon, HistoryIcon} from '@primer/octicons-react'
import {Button, Heading} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import type {ReactNode} from 'react'
import {Suspense, useCallback, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

import {IssueTimelineErrorFallback} from '../IssueTimelineErrorFallback'
import {IssueTimelineLoading} from '../IssueTimelineLoading'
import {useActivityPanel} from './ActivityPanelContext'
import styles from './TimelineActivityMenu.module.css'

export function ActivityEmptyState() {
  return (
    <Blankslate>
      <Blankslate.Visual>
        <CommentIcon size={24} />
      </Blankslate.Visual>
      <Blankslate.Heading as="h4">No activity</Blankslate.Heading>
      <Blankslate.Description>Activity on this issue will appear here.</Blankslate.Description>
    </Blankslate>
  )
}

type TimelineActivityMenuProps = {
  initiallyOpen: boolean | null
  legacyHeader: boolean
  timeline: ReactNode
  /** Controls shown next to the panel heading, such as filter and sort menus. */
  headerActions?: ReactNode
  /** Shown above the timeline. */
  notice?: ReactNode
  /** Remounts the timeline when it changes, clearing an error it has shown. */
  timelineKey?: string
}

export function TimelineActivityMenu({
  initiallyOpen,
  legacyHeader,
  timeline,
  headerActions,
  notice,
  timelineKey,
}: TimelineActivityMenuProps) {
  const {closePanel, open, openPanel, panelTarget} = useActivityPanel()
  const [overlayMounted, setOverlayMounted] = useState(initiallyOpen === true)
  const initialOpenStateAppliedRef = useRef(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (initialOpenStateAppliedRef.current || initiallyOpen === null) return
    initialOpenStateAppliedRef.current = true
    if (initiallyOpen) openPanel()
  }, [initiallyOpen, openPanel])

  const handleOpen = useCallback(() => {
    setOverlayMounted(true)
    openPanel()
    sendEvent('issue_viewer.activity_panel.opened')
  }, [openPanel])

  const panelContent = (
    <>
      {notice}
      <ErrorBoundary key={timelineKey} boundaryName="issue-activity-timeline" fallback={<IssueTimelineErrorFallback />}>
        <Suspense fallback={<IssueTimelineLoading delayedShow />}>{timeline}</Suspense>
      </ErrorBoundary>
    </>
  )
  const actions = headerActions ? <div className={styles.headerActions}>{headerActions}</div> : null

  return (
    <>
      <Button ref={buttonRef} leadingVisual={HistoryIcon} onClick={handleOpen}>
        Activity
      </Button>
      {open &&
        panelTarget &&
        createPortal(
          <Panel
            heading="Activity"
            headingLevel={2}
            headerActions={actions}
            closeButtonRef={closeButtonRef}
            returnFocusRef={buttonRef}
            onClose={closePanel}
            headerClassName={legacyHeader ? styles.legacyDockedHeader : undefined}
          >
            <Panel.Body className={styles.body}>{panelContent}</Panel.Body>
          </Panel>,
          panelTarget,
        )}
      {!panelTarget && overlayMounted && (
        <SidePanel
          open={open}
          onClose={closePanel}
          onAfterClose={() => setOverlayMounted(false)}
          returnFocusRef={buttonRef}
          initialFocusRef={headingRef}
          bodyScrollLockDisabled
          panelClassName={styles.panel}
          headerClassName={styles.header}
          closeButtonClassName={styles.closeButton}
          aria-label="Issue activity"
        >
          <SidePanel.Heading>
            <div className={styles.titleRow}>
              <Heading ref={headingRef} tabIndex={-1} as="h2" className={styles.heading}>
                Activity
              </Heading>
              {actions}
            </div>
          </SidePanel.Heading>
          <div className={styles.body}>{panelContent}</div>
        </SidePanel>
      )}
    </>
  )
}
